import cn from "classnames";
import { useState } from "react";
import {
  ChevronDown as ChevronDownIcon,
  IconProps,
} from "@rshbintech.dboul/icons";
import { Checkbox } from "../../checkbox";
import { FullNode } from "../types";
import { useMediaQuery } from "../../grid";
import { CheckboxTreeItemProps } from "./types";
import { getLeaves } from "./utils";
import styles from "./styles.module.scss";

export const CheckboxTreeItem = ({
  value,
  label,
  nodes,
  disabledNodes,
  checkedNodes,
  expandedNodes,
  isChildNode,
  dataTestId,
  onChange,
}: CheckboxTreeItemProps) => {
  const [isExpanded, setIsExpanded] = useState(
    expandedNodes?.includes(value) || false
  );

  const isNodeDisabled = disabledNodes?.includes(value) || false;
  const leavesList = nodes ? getLeaves(nodes) : [];
  const leavesItemsChecked = leavesList.filter((value) =>
    checkedNodes?.includes(value)
  ).length;

  const totalLeavesItems = leavesList.length;
  const hasLeavesItems = totalLeavesItems !== 0;

  const isIndeterminate =
    leavesItemsChecked !== 0 && leavesItemsChecked < totalLeavesItems;

  const isChecked = hasLeavesItems
    ? leavesItemsChecked === totalLeavesItems
    : checkedNodes?.includes(value) || false;

  const handleChevronIconClick = () => {
    setIsExpanded((prevExpanded) => !prevExpanded);
  };

  const handleChange = (value: string, isChecked: boolean) => {
    const updatedNodes = leavesList.concat(value);
    const node: FullNode = {
      value,
      label,
      nodes,
      checked: !isChecked,
      expanded: isExpanded,
    };

    if (isIndeterminate) {
      const isAllNotDisabledLeavesChecked = leavesList.every(
        (value) =>
          checkedNodes?.includes(value) || disabledNodes?.includes(value)
      );

      onChange?.(updatedNodes, {
        ...node,
        checked: isAllNotDisabledLeavesChecked ? isChecked : node.checked,
      });
    } else if (nodes) {
      onChange?.(updatedNodes, node);
    } else {
      onChange?.([value], node);
    }
  };

  const isMobile = useMediaQuery("mobile");
  const isXl = useMediaQuery("xl");
  const isXxl = useMediaQuery("xxl");

  let chevronIconSize: IconProps["size"];

  if (isMobile) {
    chevronIconSize = "small";
  } else if (isXl || isXxl) {
    chevronIconSize = "large";
  } else {
    chevronIconSize = "medium";
  }

  return (
    <div
      className={cn(styles["tree-container"], {
        [styles.child]: isChildNode,
      })}
    >
      <div
        className={styles["item-container"]}
        data-test-id={`${dataTestId}-${value}`}
      >
        {nodes && (
          <button
            className={styles["expand-button"]}
            data-test-id={`${dataTestId}-${value}-expand-button`}
            onClick={handleChevronIconClick}
          >
            <ChevronDownIcon
              className={cn(styles["chevron-icon"], {
                [styles.expanded]: isExpanded,
              })}
              size={chevronIconSize}
            />
          </button>
        )}

        <Checkbox
          id={value}
          name={value}
          label={label}
          value={value}
          checked={isChecked}
          indeterminate={isIndeterminate}
          disabled={isNodeDisabled}
          onChange={() => handleChange(value, isChecked)}
          dataTestId={`${dataTestId}-${value}`}
          className={cn(styles.checkbox, {
            [styles["with-chevron"]]: Boolean(nodes),
          })}
        />
      </div>
      {isExpanded &&
        nodes?.map(({ value, label, nodes }) => (
          <CheckboxTreeItem
            key={value}
            value={value}
            label={label}
            nodes={nodes}
            disabledNodes={disabledNodes}
            checkedNodes={checkedNodes}
            expandedNodes={expandedNodes}
            onChange={onChange}
            isChildNode
            dataTestId={dataTestId}
          />
        ))}
    </div>
  );
};
